import SlideIn from '@atlaskit/motion/slide-in';

import { useExitingPersistence } from '@atlaskit/motion/exiting-persistence/use-exiting-persistence';

import { useLayoutEffect, useRef, useState, type ReactNode, type TransitionEvent } from 'react';

const EXIT_DURATION_MS = 200;

/**
 * Single-message motion wrapper. Render inside `<ExitingPersistence appear>`
 * with a stable `key` (`messageInstanceId`).
 *
 * Entry: slide-up + fade-in.
 * Exit:  in-flow max-height collapse, so the chat input below glides up
 *        smoothly with the dismissed message rather than snapping.
 */
export const AnimatedMessageContainer = ({ children }: { children: ReactNode }): JSX.Element => {
	const { isExiting, onFinish } = useExitingPersistence();
	const exitRef = useRef<HTMLDivElement>(null);
	const hasStartedExit = useRef(false);
	const [exitMaxHeight, setExitMaxHeight] = useState<number | null>(null);

	// Schedule the collapse once per exit lifecycle. Short-circuit on zero
	// height; fallback timeout if onTransitionEnd never fires.
	useLayoutEffect(() => {
		if (!isExiting) {
			hasStartedExit.current = false;
			return;
		}
		if (hasStartedExit.current || !exitRef.current) return;
		hasStartedExit.current = true;

		const measured = exitRef.current.offsetHeight;
		if (measured === 0) {
			onFinish?.();
			return;
		}
		setExitMaxHeight(measured);
		const raf = requestAnimationFrame(() => setExitMaxHeight(0));
		const fallback = setTimeout(() => onFinish?.(), EXIT_DURATION_MS + 50);
		return () => {
			cancelAnimationFrame(raf);
			clearTimeout(fallback);
		};
	}, [isExiting, onFinish]);

	if (isExiting) {
		return (
			<div
				ref={exitRef}
				style={{
					overflow: 'hidden',
					maxHeight: exitMaxHeight === null ? 'none' : `${exitMaxHeight}px`,
					transition: `max-height ${EXIT_DURATION_MS}ms cubic-bezier(0.2, 0, 0, 1)`,
				}}
				// Signal ExitingPersistence to unmount once the collapse finishes.
				onTransitionEnd={(e: TransitionEvent<HTMLDivElement>) => {
					if (e.target === exitRef.current && e.propertyName === 'max-height') {
						onFinish?.();
					}
				}}
			>
				{children}
			</div>
		);
	}

	return (
		<SlideIn enterFrom="bottom" fade="in">
			{(motionProps) => (
				<div ref={motionProps.ref} className={motionProps.className}>
					{children}
				</div>
			)}
		</SlideIn>
	);
};
